import type { ReactNode } from 'react';
import { Drawer, Button } from '@viquard/doc-ui/components';
import { IcnArrowRight, IcnLink } from '@viquard/doc-ui/icons';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import { resolveKind } from '../utils/kindTheme.js';
import {
  formatDayLong,
  localDateKey,
  localTime,
  parseISODate,
} from '../utils/dateUtils.js';
import { useCalendarMessages } from '../i18n.js';
import { EventLinks } from './EventLinks.js';
import { RecurrenceBadge } from './RecurrenceBadge.js';
import { StatusBadge } from './StatusBadge.js';
import { formatEventLocation } from '../utils/location.js';

export interface EventDetailSheetProps {
  readonly event: CalendarEvent | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly onOpen?: ((event: CalendarEvent) => void) | undefined;
  /** When provided AND the event is a custom (user-created) event, the
   *  sheet renders an "Edit" affordance in the footer. Source-derived
   *  events stay read-only — the consumer is responsible for omitting
   *  this or no-op-ing for the wrong kind. */
  readonly onEdit?: ((event: CalendarEvent) => void) | undefined;
  /** When provided AND the event is a custom event, the sheet renders a
   *  "Delete" affordance. Same gating rule as `onEdit`. */
  readonly onDelete?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  /** Slot for consumer-injected extras (e.g. AttendanceRoster). */
  readonly extras?: ReactNode | undefined;
  readonly locale?: string | undefined;
  readonly viewerTz?: string | undefined;
}

/** Mobile-only event detail sheet. Slides up from the bottom; modal so
 *  it captures the screen and traps focus. Uses doc-ui Drawer.
 *
 *  Pattern: tap an event chip / card → this sheet opens. The sheet
 *  shows the full event metadata. "Open" forwards to the consumer's
 *  onSelectEvent handler; "Close" just dismisses. */
export function EventDetailSheet(props: EventDetailSheetProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    event,
    open,
    onOpenChange,
    kindTheme,
    groups,
    onOpen,
    onEdit,
    onDelete,
    onSelectLink,
    extras,
    locale,
    viewerTz,
  } = props;

  if (event === null) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        side="bottom"
        size="auto"
        modal
        title={messages.sheetAriaLabel}
      >
        <div className="vq-cal__sheet-empty" />
      </Drawer>
    );
  }

  const kind = resolveKind(kindTheme, event.kind);
  const Icon = kind.icon;
  // Bucket the event's instant onto the viewer's local day before
  // formatting — otherwise an event near midnight UTC would render the
  // wrong calendar date for viewers in negative-offset timezones.
  const date = parseISODate(localDateKey(event.startsAt, viewerTz));
  const time = localTime(event.startsAt, viewerTz);
  const group = groups?.find((g) => g.id === event.groupId);
  const locationLabel = formatEventLocation(event, messages);

  const crossPostNames =
    event.crossPostedTo !== undefined && event.crossPostedTo.length > 0 && groups !== undefined
      ? event.crossPostedTo
          .map((gid) => groups.find((g) => g.id === gid)?.name)
          .filter((n): n is string => n !== undefined)
      : [];

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      side="bottom"
      size="auto"
      modal
      aria-label={messages.sheetAriaLabel}
      footer={
        // Per-event action footer. We show:
        //   - Delete (danger) on the left for custom events
        //   - Edit (secondary) when an editor is wired and the event is custom
        //   - Open (primary) on the right when a consumer wants the
        //     detail flow to forward elsewhere (article writer, etc.)
        // Source-derived events skip Edit/Delete — the source's lifecycle
        // owns those (the route layer also enforces this with 409
        // CALENDAR_EVENT_READ_ONLY, so a misclick still fails safely).
        ((): ReactNode => {
          // Use the wire discriminator (`subjectKind`) — not `kind`,
          // which is now the visual category from the composer's
          // picker. Fall back to `kind` when the consumer hasn't
          // populated `subjectKind` to keep older integrations
          // working.
          const isCustom = (event.subjectKind ?? event.kind) === 'custom';
          const showEdit = onEdit !== undefined && isCustom;
          const showDelete = onDelete !== undefined && isCustom;
          if (!showEdit && !showDelete && onOpen === undefined) return null;
          return (
            <div className="vq-cal__sheet-footer">
              {showDelete && (
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => {
                    onDelete?.(event);
                    onOpenChange(false);
                  }}
                >
                  {messages.sheetDelete}
                </Button>
              )}
              <span style={{ flex: 1 }} />
              {showEdit && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    onEdit?.(event);
                    onOpenChange(false);
                  }}
                >
                  {messages.sheetEdit}
                </Button>
              )}
              {onOpen !== undefined && (
                <Button
                  variant="primary"
                  size="md"
                  iconRight={<IcnArrowRight size={14} />}
                  onClick={() => {
                    onOpen(event);
                    onOpenChange(false);
                  }}
                >
                  {messages.sheetOpen}
                </Button>
              )}
            </div>
          );
        })()
      }
    >
      <div className="vq-cal__sheet" style={{ ['--vq-cal-kind' as string]: kind.color }}>
        <div className="vq-cal__sheet-eyebrow">
          <span className="vq-cal__sheet-kind">
            {Icon !== undefined && <Icon size={14} />}
            <span>{kind.label}</span>
          </span>
          <RecurrenceBadge event={event} size="md" />
          {event.status !== undefined && event.status !== 'scheduled' && (
            <StatusBadge status={event.status} size="md" />
          )}
        </div>
        <h2 className="vq-cal__sheet-title">{event.title}</h2>
        <dl className="vq-cal__sheet-meta">
          <dt>{messages.sheetDate}</dt>
          <dd>{formatDayLong(date, locale)}</dd>
          {time !== null && (
            <>
              <dt>{messages.sheetTime}</dt>
              <dd className="vq-cal__sheet-meta-time">{time}</dd>
            </>
          )}
          {group !== undefined && (
            <>
              <dt>{messages.sheetGroup}</dt>
              <dd className="vq-cal__sheet-meta-group">
                <span
                  className="vq-cal__sheet-meta-dot"
                  style={group.color !== undefined ? { background: group.color } : undefined}
                  aria-hidden="true"
                />
                <span>{group.name}</span>
              </dd>
            </>
          )}
          {locationLabel !== null && (
            <>
              <dt>{messages.sheetLocation}</dt>
              <dd>{locationLabel}</dd>
            </>
          )}
          {crossPostNames.length > 0 && (
            <>
              <dt>{messages.sheetAlsoIn}</dt>
              <dd className="vq-cal__sheet-meta-cross">
                <IcnLink size={11} />
                <span>{crossPostNames.join(' · ')}</span>
              </dd>
            </>
          )}
          {event.meta !== undefined && (
            <>
              <dt>{messages.sheetNotes}</dt>
              <dd>{event.meta}</dd>
            </>
          )}
        </dl>
        <EventLinks event={event} onSelectLink={onSelectLink} />
        {extras !== undefined && <div className="vq-cal__sheet-extras">{extras}</div>}
      </div>
    </Drawer>
  );
}
